import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../environments/environment';

// Interfacce per le API di Ticketing
// Struttura conforme al DTO Java del backend
export interface TicketingAttachment {
  filename: string;
  data: string; // Base64 senza prefisso data:
  mimeType: string;
}

export interface TicketingTicketRequest {
  nome: string;
  email: string;
  subject: string;
  message: string;
  attachments?: TicketingAttachment[];
}

export interface TicketingTicketResponse {
  id: number;
  number: string;
  title: string;
  state_id: number;
  priority_id: number;
  group_id: number;
  customer_id: number;
  created_at: string;
  updated_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class TicketingService {
  // Chiama il backend AgriFlow che fa da proxy per Ticketing
  // Il backend gestisce il token API Ticketing in modo sicuro
  private readonly apiUrl = environment.backend.baseUrl + '/supporto';

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
  ) {
    this.logger.info('[Ticketing SERVICE] Configurato con apiUrl:', this.apiUrl);
  }

  getTicket(ticketId: string): Observable<TicketingTicketResponse> {
    return this.http.get<TicketingTicketResponse>(`${this.apiUrl}/${ticketId}`);
  }

  getTickets(): Observable<TicketingTicketResponse[]> {
    return this.http.get<TicketingTicketResponse[]>(this.apiUrl);
  }
  /**
   * Crea un nuovo ticket su Ticketing
   */
  createTicket(
    customerName: string,
    customerEmail: string,
    subject: string,
    message: string,
    attachments?: File[],
  ): Observable<TicketingTicketResponse> {
    this.logger.info('[Ticketing SERVICE] Creazione ticket in corso...', {
      nome: customerName,
      email: customerEmail,
      subject,
    });

    // Costruisci il payload del ticket conforme al DTO Java
    const ticketPayload: TicketingTicketRequest = {
      nome: customerName,
      email: customerEmail,
      subject: subject,
      message: message,
    };

    // Se ci sono allegati, convertili in base64
    if (attachments && attachments.length > 0) {
      return this.convertFilesToAttachments(attachments).pipe(
        map((TicketingAttachments) => {
          ticketPayload.attachments = TicketingAttachments;
          return ticketPayload;
        }),
        switchMap((payload) => this.sendTicketRequest(payload)),
        catchError((error) => {
          this.logger.error(
            '[Ticketing SERVICE] Errore durante conversione allegati:',
            error,
          );
          return throwError(() => error);
        }),
      );
    }

    // Invia la richiesta senza allegati
    return this.sendTicketRequest(ticketPayload);
  }

  /**
   * Invia la richiesta di creazione ticket tramite il backend AgriFlow
   * Il backend si occuperà di chiamare Ticketing con il token sicuro
   */
  private sendTicketRequest(
    payload: TicketingTicketRequest,
  ): Observable<TicketingTicketResponse> {
    this.logger.info(
      '[Ticketing SERVICE] Invio richiesta al backend AgriFlow...',
      payload,
    );

    return this.http
      .post<TicketingTicketResponse>(this.apiUrl, payload)
      .pipe(
        map((response) => {
          this.logger.info(
            '[Ticketing SERVICE] Ticket creato con successo:',
            response,
          );
          return response;
        }),
        catchError((error) => {
          this.logger.error(
            '[Ticketing SERVICE] Errore durante creazione ticket:',
            error,
          );
          return throwError(() => error);
        }),
      );
  }

  /**
   * Converte i file in oggetti TicketingAttachment con filename, data e mimeType
   */
  private convertFilesToAttachments(
    files: File[],
  ): Observable<TicketingAttachment[]> {
    return new Observable((observer) => {
      const attachments: TicketingAttachment[] = [];
      let filesProcessed = 0;

      files.forEach((file) => {
        const reader = new FileReader();

        reader.onload = () => {
          // Ottieni il data URL completo (es: "data:image/png;base64,iVBORw0KGgo...")
          const dataUrl = reader.result as string;

          // Estrai solo la parte base64 (rimuove il prefisso "data:...;base64,")
          const base64Data = dataUrl.split(',')[1];

          // Crea l'oggetto allegato con il formato richiesto da Ticketing
          const attachment: TicketingAttachment = {
            filename: file.name,
            data: base64Data,
            mimeType: file.type || 'application/octet-stream',
          };

          attachments.push(attachment);
          filesProcessed++;

          if (filesProcessed === files.length) {
            this.logger.info(
              `[Ticketing SERVICE] ${attachments.length} allegati convertiti:`,
              attachments.map((a) => `${a.filename} (${a.mimeType})`),
            );
            observer.next(attachments);
            observer.complete();
          }
        };

        reader.onerror = (error) => {
          this.logger.error(
            '[Ticketing SERVICE] Errore durante lettura file:',
            error,
          );
          observer.error(error);
        };

        reader.readAsDataURL(file);
      });
    });
  }

  /**
   * Ottiene la lista dei gruppi disponibili (opzionale, per configurazione futura)
   */
  getGroups(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/groups`).pipe(
      map((groups) => {
        this.logger.info('[Ticketing SERVICE] Gruppi disponibili:', groups);
        return groups;
      }),
      catchError((error) => {
        this.logger.error(
          '[Ticketing SERVICE] Errore durante recupero gruppi:',
          error,
        );
        return throwError(() => error);
      }),
    );
  }
}
